import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Save, AlertCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarRefreshTrigger } from '../contexts/SidebarRefreshContext';

interface Branch {
  id: string;
  name: string;
  location: string | null;
  companyId: string;
  createdAt: string;
}

export default function SettingsBranches() {
  const { user: currentUser } = useAuth();
  const { triggerSidebarRefresh } = useSidebarRefreshTrigger();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [userPermissions, setUserPermissions] = useState<Record<string, any>>({});

  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    location: '',
  });

  // Fetch branches and permissions on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch branches
      const branchesResponse = await api.get<any>('/branches/all');
      if (branchesResponse.branches) {
        setBranches(branchesResponse.branches);
      }

      // Fetch permissions
      try {
        const rolesRes = await api.get<any>('/roles');
        if (rolesRes.roles && Array.isArray(rolesRes.roles)) {
          const userRole = rolesRes.roles.find((r: any) => r.name === currentUser?.role);
          if (userRole && userRole.permissions) {
            const perms = typeof userRole.permissions === 'string'
              ? JSON.parse(userRole.permissions)
              : userRole.permissions;
            setUserPermissions(perms);
          }
        }
      } catch (permErr) {
        console.error('Failed to fetch permissions:', permErr);
      }
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err.message || 'Failed to load branches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      setError('');
      setIsSaving(true);

      if (!formData.name.trim()) {
        setError('Branch name is required');
        return;
      }

      if (editingId) {
        // Update existing branch
        const response = await api.put<any>(`/branches/${editingId}`, {
          name: formData.name,
          location: formData.location || null,
        });

        if (response.success && response.branch) {
          setBranches(prev =>
            prev.map(b => b.id === editingId ? response.branch : b)
          );
          setEditingId(null);
        }
      } else {
        // Create new branch
        const response = await api.post<any>('/branches', {
          name: formData.name,
          location: formData.location || null,
        });

        if (response.success && response.branch) {
          setBranches(prev => [response.branch, ...prev]);
          setShowCreateForm(false);

          // Trigger sidebar refresh to update branch selector with new branch
          await triggerSidebarRefresh();
        }
      }

      resetForm();
    } catch (err: any) {
      console.error('Failed to save branch:', err);
      setError(err.message || 'Failed to save branch');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (branch: Branch) => {
    setEditingId(branch.id);
    setFormData({
      name: branch.name,
      location: branch.location || '',
    });
    setShowCreateForm(false);
  };

  const handleDelete = async (branchId: string) => {
    try {
      setError('');
      setIsDeleting(true);

      await api.delete(`/branches/${branchId}`);

      setBranches(prev => prev.filter(b => b.id !== branchId));
      setDeleteConfirm(null);
    } catch (err: any) {
      console.error('Failed to delete branch:', err);
      setError(err.message || 'Failed to delete branch');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    setDeleteConfirm(null);
  };

  const resetForm = () => {
    setFormData({ name: '', location: '' });
    setEditingId(null);
    setShowCreateForm(false);
    setError('');
  };

  const canView = userPermissions.branches?.view === true;
  const canCreate = userPermissions.branches?.create === true;
  const canEdit = userPermissions.branches?.edit === true;
  const canDelete = userPermissions.branches?.delete === true;

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">Branches</h1>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              You don't have permission to view branches. Contact your company owner to enable access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Branches</h1>
          <p className="text-gray-500 mt-1">Manage company branches and locations</p>
        </div>
        {canCreate && !showCreateForm && !editingId && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Branch
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Create Form */}
      {(showCreateForm || editingId) && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4">
            {editingId ? 'Edit Branch' : 'Create New Branch'}
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Branch Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isSaving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                placeholder="Enter branch name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={isSaving}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 disabled:opacity-50"
                placeholder="Enter location (optional)"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branches List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-900">
            Branches ({branches.length})
          </h2>
        </div>

        {branches.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <p className="text-gray-500">No branches created yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {branches.map(branch => (
              <div key={branch.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{branch.name}</h3>
                    {branch.location && (
                      <p className="text-sm text-gray-600 truncate mt-1">{branch.location}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">
                      Created {new Date(branch.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0">
                    {canEdit && (
                      <button
                        onClick={() => handleEdit(branch)}
                        disabled={isSaving || editingId !== null}
                        className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Edit branch"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => setDeleteConfirm(branch.id)}
                        disabled={isSaving || editingId !== null}
                        className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Delete branch"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setDeleteConfirm(null)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl z-50 w-96 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Delete Branch</h3>
            <div className="text-gray-600 text-sm mb-6 space-y-2">
              <p>Are you sure you want to delete this branch?</p>
              <p className="text-xs text-gray-500">This action cannot be undone.</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isDeleting}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
